import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DEFAULT_MODE, DEFAULT_THEME, getTheme, THEMES } from "./themes";

const STORAGE_KEY = "portfolio-theme";

const ThemeContext = createContext(null);

const readStored = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (_) {
    /* ignore */
  }
  return null;
};

const systemPrefersDark = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-color-scheme: dark)").matches;

export const ThemeProvider = ({ children }) => {
  const stored = readStored();

  const [theme, setTheme] = useState(stored?.theme || DEFAULT_THEME);
  const [mode, setMode] = useState(
    stored?.mode || (systemPrefersDark() ? "dark" : "light") || DEFAULT_MODE
  );

  // Reflect state onto <html> so CSS variables resolve, and persist choice.
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-theme", theme);
    root.setAttribute("data-mode", mode);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ theme, mode }));
    } catch (_) {
      /* ignore */
    }
  }, [theme, mode]);

  const toggleMode = useCallback(
    () => setMode((m) => (m === "dark" ? "light" : "dark")),
    []
  );

  const selectTheme = useCallback((id) => {
    const next = getTheme(id);
    setTheme(next.id);
    setMode((m) => (next.modes.includes(m) ? m : next.modes[0]));
  }, []);

  const value = useMemo(
    () => ({ theme, mode, themes: THEMES, toggleMode, setMode, selectTheme }),
    [theme, mode, toggleMode, selectTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
};
